import AnimationComponent from 'Component/Animation';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import Game from 'Game';
import CleanupManager from 'Utility/CleanupManager';
import { UIActions } from 'State/UI/UIActions';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import StateComponent from 'Component/State';
import { IGameState } from 'State/Game/IGameState';
import Renderable from 'Component/Renderable';
import DateHelper from 'Utility/DateHelper';
import { questSlotsCount, questTimerDuration } from 'Constants/Quests';
import Scene from 'Scene/Scene';
import { StartAnimation } from 'System/Animation/AnimationHelper';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import { IPlayerStateQuestData } from 'State/Player/IPlayerState';
import { PlayerActions } from 'State/Player/PlayerActions';
import Timer from 'System/Timer/Timers/Timer';
import { PopupID } from 'State/UI/PopupID';
import { QuestStateHelper } from 'State/Player/Quests/QuestStateHelper';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { QuestButtonLayout } from 'Layouts/WorldHud/QuestsButtonLayout';
import TextComponent from 'Component/Render/Text';
import RelativePosition from 'Component/Render/RelativePosition';
import TransformComponent from 'Component/Transform';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import WorldHUDHandler from 'System/UI/HUD/WorldHUDHandler';
import { trayClosedPivot } from 'System/Animation/Clip';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import getEvent = EventStateHelper.getEvent;
import { EventQuestButtonLayout, isCurrentEvent } from 'Constants/Events/CurrentEvent';

enum QuestButtonStatus {
    ALL_IN_PROGRESS = 'allInProgress',
    SOME_COMPLETED = 'someCompleted',
}

export const ShowQuestProgressEvent: string = 'showQuestProgress';

export default class QuestButton extends GameObject<PIXI.Container> {
    private readonly cleanupManager: CleanupManager = new CleanupManager();

    private status: QuestButtonStatus;
    private progressUpdates: IPlayerStateQuestData[] = [];
    private startedProgressAnimation: boolean = false;
    private readonly timers: Timer[] = [];
    private currentTimerIndex: number;
    private layout: GLayout<QuestButtonLayout | EventQuestButtonLayout>;

    constructor() {
        super();
    }

    public onCreate(props: { scene: Scene }): void {
        this.name = WorldHUDHandler.QUESTS_BTN_NAME;
        const isEventActive = isCurrentEvent(getEvent(Game.store.getState(), PopupID.QUEST_MENU));
        this.layout = getLayout(isEventActive ? EventQuestButtonLayout : QuestButtonLayout);
        this.addChild(this.layout.root);
        this.layout.root.addComponent(RelativePosition, { x: 0.39, y: 0.928 });

        this.layout.root.addComponent(AnimationComponent);
        this.layout.mission_tray_container.addComponent(AnimationComponent);

        // Add button functionality to each button in each state.
        [
            this.layout.mission_button_base_active,
            this.layout.mission_button_base_timer,
            this.layout.mission_button_base_reward,
        ].forEach(button => {
            button.on(GameObjectEvent.SELECTED, this.onClick);
            this.cleanupManager.add(() => {
                button.off(GameObjectEvent.SELECTED, this.onClick);
            });
            applyDefaultButtonAnimation(button);
        });

        this.layout.root.addComponent(StateComponent, {
            handleStateChanged: this.handleStateChanged,
        });

        this.handleStateChanged(Game.store.getState());

        props.scene.on(ShowQuestProgressEvent, this.queueQuestProgressAnimation);
        this.cleanupManager.add(() => {
            props.scene.off(ShowQuestProgressEvent, this.queueQuestProgressAnimation);
        });

        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);

        // Set initial pivot so the tray is hidden.
        this.layout.mission_tray_container.getComponent(TransformComponent).pivot = trayClosedPivot;

        this.handleResize();
    }
    private readonly onClick = () => {
        UIActions.showPopup(PopupID.QUEST_MENU, {});
    };
    private readonly queueQuestProgressAnimation = (questData: IPlayerStateQuestData) => {
        this.progressUpdates.push(questData);
        this.startAnimationIfNeeded();
    };
    private readonly handleStateChanged = (state: IGameState) => {
        this.updateStatus(state);
        const quests = state.playerState.quests;
        this.updateQuestPanelText(quests.length, quests.length, QuestStateHelper.canCompleteAnyQuest(state));
        this.startAnimationIfNeeded();
    };
    private updateStatus(state: IGameState) {
        // Get the current status of the button
        const status = getQuestStatus(state);

        // If status is upto date, do nothing
        if (status === this.status) return;

        // Update status
        this.status = status;

        // Set button texture
        switch (status) {
            case QuestButtonStatus.ALL_IN_PROGRESS:
                this.layout.mission_button_base_active.getComponent(Renderable).visible = true;
                this.layout.mission_button_base_timer.getComponent(Renderable).visible = false;
                this.layout.mission_button_base_reward.getComponent(Renderable).visible = false;
                break;
            case QuestButtonStatus.SOME_COMPLETED:
                this.layout.mission_button_base_active.getComponent(Renderable).visible = false;
                this.layout.mission_button_base_timer.getComponent(Renderable).visible = false;
                this.layout.mission_button_base_reward.getComponent(Renderable).visible = true;

                break;
        }
    }
    private updateQuestPanelText(active: number, total: number, canClaimReward: boolean) {
        const missionLangStr: string = Game.locale.getText('Quests');
        if (canClaimReward) {
            this.layout.mission_button_title_reward.getComponent(TextComponent).text = `${Game.locale.getText(
                'ClaimReward'
            )}`;
        } else if (active === total) {
            this.layout.mission_button_title_active.getComponent(TextComponent).text = `${missionLangStr}`;
        } else {
            this.layout.mission_button_title_timer.getComponent(
                TextComponent
            ).text = `${missionLangStr} (${active}/${total})`;
        }
    }
    private startAnimationIfNeeded() {
        if (!this.layout.root.destroyed && !this.startedProgressAnimation && this.progressUpdates.length > 0) {
            const getNextUpdate = () => {
                return this.progressUpdates.pop();
            };
            this.startedProgressAnimation = true;
            StartAnimation(this.layout.mission_tray_container, 'UpdateQuestProgress', {
                layout: this.layout,
                getNextUpdate,
            })?.once(AnimationClipEvent.DESTROY, () => {
                this.startedProgressAnimation = false;
                this.startAnimationIfNeeded();
            });
        }
    }

    private readonly handleResize = () => {
        this.layout.root.getComponent(TransformComponent).y = PIXIRenderer.getOffsetY() * 0.85;
    };

    public destroy() {
        this.cleanupManager.cleanUpAll();
        super.destroy();
    }
}

function getQuestStatus(state: IGameState): QuestButtonStatus {
    if (QuestStateHelper.canCompleteAnyQuest(state)) {
        return QuestButtonStatus.SOME_COMPLETED;
    }
    return QuestButtonStatus.ALL_IN_PROGRESS;
}
